import {useCallback, useMemo, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {SkillPathStatus} from 'gql/types';
import {useAllAdaptiveSubjects} from 'client/TeacherDashboard/Home/hooks/useHomeAdaptiveSubjects';
import type {SortValue} from 'client/TeacherDashboard/types';
import {normalizeError} from 'lib/normalizeError';

import type {SkillPathEntryFieldsFragment} from '../queries/SkillPaths.queries.generated';
import {useSkillPathEntriesQuery} from '../queries/SkillPaths.queries.generated';
import type {
  SkillPathRowData,
  SkillPathRowStatus,
  SkillPathSortValue,
  SkillPathStatusSegment,
  SkillPathSubjectOption
} from '../types';
import {scopedDateOverride} from '../rowDateScope';
import type {SharedSkillPathFilters} from './skillPathEntryFilters';
import {
  buildEntryFilter,
  buildEntrySort,
  toClassroomIds,
  toStatusArg
} from './skillPathEntryFilters';

const PAGE_SIZE = 25;

/** Stable identities, so an empty result doesn't read as a new one to consumers. */
const NO_ROWS: SkillPathRowData[] = [];
const NO_ENTRIES: SkillPathEntryFieldsFragment[] = [];

const ROW_STATUS: Record<SkillPathStatus, SkillPathRowStatus> = {
  [SkillPathStatus.Open]: 'open',
  [SkillPathStatus.Closed]: 'closed',
  [SkillPathStatus.Scheduled]: 'scheduled'
};

/**
 * A row's own status, which decides its card treatment and which date pair it renders. Only All
 * asks the server for it per row; every other tab pins it in the filter, so the field is absent
 * there by design and the segment itself is the answer. Falls back rather than indexing blind, so
 * a status this bundle predates cannot reach the row as `undefined`.
 *
 * Not shared with the diagnostics list's equivalent: that query selects the status on every tab, so
 * it reads the server field first where this one reads the segment first.
 */
const toRowStatus = (
  entry: SkillPathEntryFieldsFragment,
  segment: SkillPathStatusSegment
): SkillPathRowStatus => {
  if (segment !== 'all') {
    return segment;
  }
  return (entry.skillPathStatus && ROW_STATUS[entry.skillPathStatus]) ?? 'open';
};

export interface SkillPathsListFilters extends SharedSkillPathFilters {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  sort: SortValue<SkillPathSortValue>;
}

interface Result {
  rows: SkillPathRowData[];
  subjectOptions: SkillPathSubjectOption[];
  isLoading: boolean;
  hasError: boolean;
  hasNextPage: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

type RowDates = Pick<
  SkillPathRowData,
  'minDueDate' | 'maxDueDate' | 'minStartDate' | 'maxStartDate'
>;

const toRowDates = (
  entry: SkillPathEntryFieldsFragment,
  segment: SkillPathStatusSegment,
  status: SkillPathRowStatus
): RowDates => ({
  minDueDate: entry.skillPathMinDueDate ?? null,
  maxDueDate: entry.skillPathMaxDueDate ?? null,
  minStartDate: entry.skillPathMinStartDate ?? null,
  maxStartDate: entry.skillPathMaxStartDate ?? null,
  ...scopedDateOverride(entry, segment, status)
});

const toRow = (
  entry: SkillPathEntryFieldsFragment,
  segment: SkillPathStatusSegment
): SkillPathRowData => {
  const status = toRowStatus(entry, segment);
  return {
    id: entry.id,
    skillName: entry.name,
    code: entry.code ?? '',
    guideId: entry.guideId,
    subjectId: entry.subjectId,
    // `parent` is the topic, its own parent the theme.
    topicId: entry.parent?.id,
    themeId: entry.parent?.parent?.id,
    onStudentsSkillPaths: entry.onStudentsSkillPaths ?? false,
    status,
    ...toRowDates(entry, segment, status),
    studentCount: entry.skillPathStudentCount,
    completedStudentCount: entry.skillPathCompletedStudentCount
  };
};

export const useSkillPathsList = ({
  classroomId,
  segment,
  sort,
  ...shared
}: SkillPathsListFilters): Result => {
  const {search, subject, dueWithin, dueDateStart, dueDateEnd} = shared;

  const variables = useMemo(
    () => ({
      classroomIds: toClassroomIds(classroomId),
      status: toStatusArg(segment),
      // Which date pair the row fragment asks for, and whether it also asks each row's own status
      // plus the per-status ranges only All needs.
      isAllStatuses: segment === 'all',
      includeDueDates: segment !== 'scheduled',
      includeStartDates: segment === 'scheduled',
      filter: buildEntryFilter({search, subject, dueWithin, dueDateStart, dueDateEnd}, segment),
      sort: buildEntrySort(sort, segment),
      limit: PAGE_SIZE
    }),
    [classroomId, segment, sort, search, subject, dueWithin, dueDateStart, dueDateEnd]
  );

  const {data, loading, error, refetch, fetchMore} = useSkillPathEntriesQuery({
    variables,
    fetchPolicy: 'cache-and-network'
  });

  // Every async pagination write is keyed to the request that produced it, and reads ignore any
  // key but the current one — so a response landing after a filter change (or a refetch, via the
  // generation) is inert by construction rather than filtered by a guard that could race.
  const [refetchGen, setRefetchGen] = useState(0);
  const variablesKey = `${refetchGen}:${JSON.stringify(variables)}`;
  // Pages past the first accumulate here: the Apollo field is argument-keyed, so it cannot merge
  // them itself without a typePolicy.
  const [morePages, setMorePages] = useState({key: '', entries: NO_ENTRIES});
  // The watched query only ever holds page one, whose `hasNextPage` stays true; the page that says
  // there is nothing left is the last one `fetchMore` fetched.
  const [exhaustedKey, setExhaustedKey] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // A variables change starts a new result set; clear the paging flag so the new list doesn't
  // render a "loading more" that isn't happening, blocking its own first `loadMore`.
  const [renderedVariablesKey, setRenderedVariablesKey] = useState(variablesKey);
  if (renderedVariablesKey !== variablesKey) {
    setRenderedVariablesKey(variablesKey);
    if (isLoadingMore) {
      setIsLoadingMore(false);
    }
  }

  const moreEntries = morePages.key === variablesKey ? morePages.entries : NO_ENTRIES;

  const page = data?.skillPathEntries;
  const firstPageResults = page?.results;
  const entries = useMemo(
    () => [...(firstPageResults ?? []), ...moreEntries],
    [firstPageResults, moreEntries]
  );

  const rows = useMemo(
    () => (entries.length > 0 ? entries.map((entry) => toRow(entry, segment)) : NO_ROWS),
    [entries, segment]
  );

  // The subject filter lists the whole adaptive catalog, not only subjects the teacher has paths in.
  const {subjects: adaptiveSubjects} = useAllAdaptiveSubjects();
  const subjectOptions = useMemo<SkillPathSubjectOption[]>(
    () => adaptiveSubjects.map((s) => ({id: s.id, name: s.name})),
    [adaptiveSubjects]
  );

  const hasNextPage = exhaustedKey !== variablesKey && Boolean(page?.hasNextPage);

  const loadMore = useCallback(() => {
    // `firstPageResults` rather than `loading`: page one for these variables is what the offset
    // counts from, and `loading` is also true while a warm cache revalidates it.
    if (!firstPageResults || isLoadingMore || !hasNextPage) {
      return;
    }
    const requestedKey = variablesKey;
    setIsLoadingMore(true);
    fetchMore({variables: {...variables, offset: entries.length}})
      .then((result) => {
        const nextPage = result.data?.skillPathEntries;
        if (nextPage?.hasNextPage === false) {
          setExhaustedKey(requestedKey);
        }
        const next = nextPage?.results ?? [];
        // De-duplicated: an edit between pages shifts offsets and can repeat a row.
        setMorePages((current) => {
          const base = current.key === requestedKey ? current.entries : NO_ENTRIES;
          const seen = new Set([
            ...entries.map((entry) => entry.id),
            ...base.map((entry) => entry.id)
          ]);
          return {key: requestedKey, entries: [...base, ...next.filter((e) => !seen.has(e.id))]};
        });
      })
      .catch((fetchMoreError) => {
        // Toast rather than blank the list — the rows already on screen are still good.
        addToast({
          color: 'negative',
          title: 'Couldn’t load more skill paths',
          message: normalizeError(fetchMoreError).message
        });
      })
      .finally(() => setIsLoadingMore(false));
  }, [entries, fetchMore, firstPageResults, hasNextPage, isLoadingMore, variables, variablesKey]);

  const handleRefetch = useCallback(() => {
    // Bumping the generation retires every key: accumulated pages stop being read and any in-flight
    // terminal page can no longer mark the fresh result set exhausted.
    setRefetchGen((generation) => generation + 1);
    // Swallow the rejection: the failure is surfaced reactively via `error`.
    void refetch().catch(() => {});
  }, [refetch]);

  return {
    rows,
    subjectOptions,
    isLoading: loading && !data,
    hasError: Boolean(error),
    hasNextPage,
    isLoadingMore,
    loadMore,
    refetch: handleRefetch
  };
};
